import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import useSidebar from 'hooks/useSidebar';
import { useEffect, useMemo, useRef, useState } from 'react';
import AnimateHeight, { Height } from 'react-animate-height';
import styles from './Sidebar.module.scss';
import { getHeaderHeight } from '@/store/global';
import { useSelector } from 'react-redux';

const Sidebar = ({ fields }) => {
  const sidebarDropdown = useRef(null);
  const sidebarWrapper = useRef(null);
  const [height, setHeight] = useState<Height>(0);
  const headerHeight = useSelector(getHeaderHeight);

  const {
    setShowScroll,
    activeSubtitle,
    showDropdown,
    dropdownHeight,
    sidebarWidth,
    scrolling,
    scrollTo,
    setDropdownHeightValue,
    setSidebarWidthValue,
    observeHeight,
  } = useSidebar({
    dropdownRef: sidebarDropdown,
    wrapperRef: sidebarWrapper,
    firstId: fields.items[0].id,
    items: fields.items,
  });

  const getActiveSubtitleName: string = useMemo(() => {
    return fields.items.filter((item) => item.id === activeSubtitle)[0]?.fields.title.value;
  }, [activeSubtitle, fields.items]);

  useEffect(() => {
    setSidebarWidthValue(sidebarWrapper);
    if (sidebarDropdown.current) {
      setDropdownHeightValue();
      observeHeight(sidebarDropdown);
      window.addEventListener('resize', setSidebarWidthValue(sidebarWrapper));
    }
    return () => {
      window.removeEventListener('resize', setSidebarWidthValue);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="col-12 col-md-6 col-xl-3 realtive">
      <div ref={sidebarWrapper} className={`${styles['sidebar-wrapper']} mb-6 mb-xl-0`}>
        <nav style={{ width: `${sidebarWidth}px` }}>
          <Text tag="h3" className="mb-5" field={fields.title} />
          <div className="flex flex-column w-100">
            {fields.items.map((title, i) => (
              <div key={`title-${title.id}-${i}`}>
                {title.fields.title && title.fields.title.value && (
                  <p
                    className={`${styles['title']} text-uppercase py-1 ps-3 ${
                      activeSubtitle === title.id ? styles['active'] : ''
                    }`}
                    tabIndex="0"
                    onClick={() => scrollTo(title.id, title.id)}
                    onKeyDown={(e) => {
                      if (e.code === 'Enter') {
                        scrollTo(title.id, title.id);
                      }
                    }}
                  >
                    {title.fields.title.value}
                  </p>
                )}
                {title.fields.items.map(
                  (subtitle) =>
                    subtitle.fields.subsectionTitle &&
                    subtitle.fields.subsectionTitle.value &&
                    activeSubtitle === title.id &&
                    !scrolling && (
                      <p
                        key={`subtitle-${subtitle.id}-${i}`}
                        tabIndex="0"
                        className={`${styles['subtitle']} ps-5 pb-1`}
                        onClick={() => scrollTo(subtitle.id, title.id)}
                        onKeyDown={(e) => {
                          if (e.code === 'Enter') {
                            scrollTo(subtitle.id, title.id);
                          }
                        }}
                      >
                        {subtitle.fields.subsectionTitle.value}
                      </p>
                    )
                )}
              </div>
            ))}
          </div>
        </nav>
      </div>
      {showDropdown && (
        <div
          className={`${styles['sidebar-dropdown']} flex-column`}
          style={{ top: `${headerHeight}px` }}
        >
          <button
            ref={sidebarDropdown}
            aria-expanded="false"
            className={`${styles['sidebar-dropdown-btn']} ${styles['collapsed']} dropdown-button pe-2`}
            type="button"
            onClick={() => {
              setShowScroll((prev) => !prev);
              setHeight(height === 0 ? 'auto' : 0);
            }}
          >
            <div className="d-flex align-items-center container expandable">
              <p className={`text-left text-uppercase ${styles['dropdown-spacing']} text-black`}>
                {getActiveSubtitleName}
              </p>
              <em
                className={`d-flex bi-chevron-${height ? 'up' : 'down'} ms-auto ms-sm-3`}
                aria-hidden="true"
              />
            </div>
          </button>

          <AnimateHeight height={height} duration={200}>
            <div className={`collapse background-white ${styles['sidebar-dropdown-content']}`}>
              <div
                className="pb-1"
                style={{ maxHeight: `calc(100vh - ${headerHeight + dropdownHeight}px)` }}
              >
                {fields.items.map((title) => (
                  <>
                    <div key={`title-second-${title.id}`}>
                      {title.fields.title && title.fields.title.value && (
                        <div
                          className={`title dropdown-title dropdown-spacing text-uppercase py-2 ${
                            activeSubtitle === title.id ? styles['active'] : ''
                          }`}
                          tabIndex="0"
                          onClick={() => {
                            scrollTo(title.id, title.id);
                            sidebarDropdown.current.click();
                          }}
                          onKeyDown={(e) => {
                            if (e.code === 'Enter') {
                              scrollTo(title.id, title.id);
                            }
                          }}
                        >
                          <div className="container">{title.fields.title.value}</div>
                        </div>
                      )}
                    </div>
                    {title.fields.items.map((subtitle) => (
                      <div
                        key={`subtitle-second-${subtitle.id}`}
                        tabIndex="0"
                        className={`${styles['dropdown-subtitle']} ps-2 pb-2 ${
                          activeSubtitle === title.id ? styles['active'] : ''
                        }`}
                        onClick={() => {
                          scrollTo(subtitle.id, title.id);
                          sidebarDropdown.current.click();
                        }}
                        onKeyDown={(e) => {
                          if (e.code === 'Enter') {
                            scrollTo(subtitle.id, title.id);
                          }
                        }}
                      >
                        {subtitle.fields.subsectionTitle &&
                          subtitle.fields.subsectionTitle.value && (
                            <div className="container">{subtitle.fields.subsectionTitle.value}</div>
                          )}
                      </div>
                    ))}
                  </>
                ))}
              </div>
            </div>
          </AnimateHeight>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
